<template>
    <!-- eslint-disable vue/html-indent  -->
    <div class="data-charts-module">
        <div v-if="!isLoading" id="chart-container" class="chart-container">
            <component
                :is="chart.component"
                v-for="chart in analyticsData.graphs"
                :key="chart.id"
                :loading-more="loadingMore"
                v-bind="chartProperties(chart)"
                @on-click-table-data="$emit('on-click-table-data', $event)"
                @on-click-table-inner-data="
                    $emit('on-click-table-inner-data', $event)
                "
                @pagination-changed="$emit('pagination-changed', $event)"
            >
                <template v-slot:header="{ header }">
                    <div class="flex justify-between items-center">
                        <div
                            :class="[
                                { 'flex items-center': hasIcon(header) },
                                'p-3',
                            ]"
                        >
                            <component
                                :is="header.icon.name"
                                v-if="hasIcon(header)"
                                aria-hidden="true"
                                class="mr-3"
                                :style="
                                    header.icon.name === 'ChartSquareBarIcon'
                                        ? 'transform: scaleY(-1) rotate(90deg);'
                                        : ''
                                "
                                v-bind="getIconStyles(header.icon)"
                            />
                            <h3
                                v-if="chart.title"
                                v-bind="header.title"
                                :class="header"
                            >
                                {{ chart.title }}
                            </h3>
                            <p
                                v-if="chart.description"
                                v-bind="header.description"
                                class="description"
                            >
                                {{ chart.description }}
                            </p>
                        </div>
                        <div v-if="isDateSelectionEnable(chart)" style="position:absolute;right:115px;top:5px">
                            <DateSelection
                                :options="chart.data.options_filter"
                                @selected-month="onSelectTableMonth"
                            />
                        </div>
                        <div
                            v-if="
                                chart.type === 'table' &&
                                !_.isEmpty(chart.export_url)
                            "
                            class="pb-10"
                        >
                            <ButtonComponent
                                v-bind="buttonProps"
                                :disabled="
                                    _.isEmpty(chart.data) ||
                                    _.isEmpty(chart.data.data)
                                "
                                @on-click="exportReport(chart.id)"
                            >
                                <template v-slot:prepend>
                                    <DownloadIcon aria-hidden="true" />
                                </template>
                            </ButtonComponent>
                            <a
                                :id="chart.id"
                                :href="chart.export_url"
                                target="_blank"
                                class="d-none"
                            />
                        </div>
                    </div>
                </template>
            </component>
        </div>
        <PlaceholderComponent
            v-else type="chart"
            :items="settings?.placeholder ?? 5"
            :random="!settings?.placeholder"
        />

        <!-- for watermark, this will be remove after mounted -->
        <div ref="watermarkWrapperNode" class="watermark-wrapper">
            <p ref="emailmarkNode">
                {{ email }}
            </p>
            <div ref="watermarkNode" class="watermark" />
        </div>
    </div>
</template>
<script>
import DashboardLayoutComponent from "@Layouts/dashboard/";
import { useCookies } from "vue3-cookies";
import { Tokens } from "@Utilities/enum";
import { useRoute } from 'vue-router';
import {
    DoughnutChartComponent,
    BarChartComponent,
    TableChartComponent,
    SinglestatComponent,
    LineChartComponent,
    MixChartComponent,
    PieChartComponent,
} from "@DataChartsModule/components";
import { PlaceholderComponent } from "@CommonComponents";
import { useStore } from "vuex";
import useStyles from "@Functions/useStyles.js";
import useDashboard from "@Functions/useDashboard.js";
import {
    DownloadIcon,
    ChartBarIcon,
    ChartPieIcon,
    PresentationChartLineIcon,
    ChartSquareBarIcon,
    MapIcon,
    TableIcon,
} from "@heroicons/vue/outline";
import { TrendingUpIcon } from "@heroicons/vue/solid";
import { computed, defineAsyncComponent, onMounted, ref } from "vue";
import domtoimage from "dom-to-image";
const DateSelection = defineAsyncComponent(() =>
    import("../../data-charts/components/table/filter/date-selection"))

export default {
    name: "ChartsModule",
    components: {
        DashboardLayoutComponent,
        DoughnutChartComponent,
        BarChartComponent,
        TableChartComponent,
        SinglestatComponent,
        LineChartComponent,
        MixChartComponent,
        PieChartComponent,
        DownloadIcon,
        ChartBarIcon,
        ChartPieIcon,
        PresentationChartLineIcon,
        ChartSquareBarIcon,
        TrendingUpIcon,
        MapIcon,
        TableIcon,
        PlaceholderComponent,
        DateSelection
    },
    props: {
        analyticsData: { type: Object, default: null },
        isLoading: Boolean,
        loadingMore: Boolean,
    },
    emits: [
        "on-click-table-data",
        "on-click-table-inner-data",
        "pagination-changed",
    ],
    setup(props, { emit }) {
        const store = useStore();
        const { cookies } = useCookies();
        const route = useRoute()
        const { settings } = useDashboard();
        const { email } = window.User;
        const watermarkNode = ref(null);
        const watermarkWrapperNode = ref(null);
        const emailmarkNode = ref(null);
        const watermarkData = ref(null);

        const getWatermarkImageData = async () => {
            await domtoimage
                .toPng(emailmarkNode.value)
                .then((dataUrl) => {
                    if (dataUrl) {
                        if (watermarkNode.value) {
                            watermarkNode.value.style.backgroundImage = `url(${dataUrl})`;
                        }
                    }
                })
                .catch(function (error) {
                    console.error("oops, watermark went wrong!", error);
                });
            await domtoimage
                .toPng(watermarkNode.value)
                .then((dataUrl) => {
                    watermarkData.value = dataUrl;
                    store.commit("Access/setWatermarkImage", dataUrl);
                })
                .catch(function (error) {
                    console.error("oops, watermark went wrong!", error);
                });

            setTimeout(() => {
                (watermarkWrapperNode.value ? watermarkWrapperNode.value.remove() : '')
            }, 500);
        };

        const { setContainerClass, getIconStyles } = useStyles();

        const buttonProps = computed(() => {
            return {
                type: "primary-btn",
                label: "Export",
                class: "absolute right-0",
            };
        });

        const hasIcon = (header) => {
            return (
                _.has(header, "icon") &&
                _.has(header.icon, "name") &&
                header.icon.name
            );
        };

        onMounted(async () => {
            await getWatermarkImageData();
        });

        const exportReport = (chart_id) => {
            document.getElementById(chart_id).click();
        };

        const setDefaultClass = (chart) => {
            let item = "";
            if (!chart.container?.width) {
                item = chart.type === "table" ? "col-span-12" : "col-span-6";
            }
            return item;
        };

        const chartProperties = (chart) => {
            let item = {
                isLoading: props.isLoading,
                class: [
                    setDefaultClass(chart),
                    setContainerClass(chart.container)?.class,
                ],
                style: setContainerClass(chart.container)?.style,
                watermarkData: watermarkData.value,
            };

            return _.merge(_.cloneDeep(chart), item);
        };

        const onClickTableData = (data) => {
            emit("on-click-table-data", data);
        };

        const onSelectTableMonth = (data) => {
            let { params } = route
            store.dispatch("TableAnalytics/updateAnalyticsData", { data, attribute: params.attribute });
        }

        const isDateSelectionEnable = (chart) => {
            let saveSections = cookies.get(Tokens.MenuNav);
            return chart.type === 'table' &&
                chart.data.isVertical &&
                ( saveSections.current === 'Indications' ||
                saveSections.current === 'Specialties' )
        }

        return {
            onClickTableData,
            getIconStyles,
            exportReport,
            hasIcon,
            buttonProps,
            chartProperties,
            watermarkWrapperNode,
            watermarkNode,
            emailmarkNode,
            email,
            onSelectTableMonth,
            isDateSelectionEnable,
            settings,
        };
    },
};
</script>

<style lang="scss">
.data-charts-module {
    .chart-container__wrapper__data {
        @apply w-full;
        .chart-wrapper {
            @apply flex items-center justify-center h-full;
        }
    }
    .chart-container__wrapper__header {
        @apply p-0;
    }
    .spinner-component {
        @apply flex items-center justify-center;
        min-height: 67vh;
    }
    .watermark-wrapper {
        p {
            @apply inline-block p-6 text-gray-100;
        }
        .watermark {
            @apply relative origin-center -z-10;
            transform: rotate(-45deg) translateX(0%) translateY(-50%);
            width: 300%;
            height: 200vh;
            min-height: 1000px;
        }
    }
}
@screen sm {
    .data-charts-module {
        .watermark-wrapper {
            .watermark {
                transform: rotate(-45deg) translateX(0%) translateY(-70%);
            }
        }
    }
}
@screen lg {
    .data-charts-module {
        .watermark-wrapper {
            .watermark {
                transform: rotate(-45deg) translateX(0%) translateY(-105%);
            }
        }
    }
}
</style>
